import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { DateTime, Duration } from 'luxon';
import { fromEvent, merge, Observable, Subscription, throttleTime } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Session } from '../../shared/models/identity.model';
import { SessionService } from './session.service';
import { SnackbarService } from './snackbar.service';

const USER_ACTIVITY_EVENT_TYPES: Readonly<string[]> = ['click', 'keydown', 'touchstart', 'scroll'];

function createUserActivityObservable(): Observable<Event> {
	return merge(...USER_ACTIVITY_EVENT_TYPES.map((eventType: string) => fromEvent(document, eventType)));
}

@Injectable({
	providedIn: 'root',
})
export class SessionRefreshService {
	private httpClient: HttpClient = inject(HttpClient);
	private snackBar: SnackbarService = inject(SnackbarService);
	private sessionService: SessionService = inject(SessionService);

	private shouldSendWFKeepAliveRequests: boolean;

	private sessionStartedAt: DateTime;

	private userLastActiveAt: DateTime;
	private userActivityWindow: Duration;
	private userActivitySubscription: Subscription;

	private earlyRefreshEnabled: boolean;
	private earlyRefreshTimeoutDuration: Duration;
	private earlyRefreshTimeout?: ReturnType<typeof setTimeout>;
	private earlyRefreshTimeoutExpired: boolean;

	private expirationWarningDuration: Duration;
	private expirationWarningTimeout?: ReturnType<typeof setTimeout>;
	private expirationWarningSnackBarRef?: MatSnackBarRef<TextOnlySnackBar>;
	private expirationWarningTimeoutExpired: boolean;

	private expirationTimeout?: ReturnType<typeof setTimeout>;

	constructor() {
		this.shouldSendWFKeepAliveRequests = false;

		this.sessionStartedAt = DateTime.now();

		this.userLastActiveAt = DateTime.now();
		this.userActivityWindow = Duration.fromObject({ minutes: 5 });

		this.earlyRefreshEnabled = false;
		this.earlyRefreshTimeoutDuration = Duration.fromObject({ minutes: 5 });
		this.earlyRefreshTimeoutExpired = false;

		this.expirationWarningDuration = Duration.fromObject({ minutes: 1 });
		this.expirationWarningTimeoutExpired = false;

		this.userActivitySubscription = createUserActivityObservable()
			.pipe(takeUntilDestroyed(), throttleTime(1000))
			.subscribe(() => {
				this.userLastActiveAt = DateTime.now();

				if (this.earlyRefreshTimeoutExpired || this.expirationWarningTimeoutExpired) {
					this.refreshSession();

					this.earlyRefreshTimeoutExpired = false;
					this.expirationWarningTimeoutExpired = false;
					this.expirationWarningSnackBarRef?.dismiss();
				}
			});

		this.sessionService.session$.pipe(takeUntilDestroyed()).subscribe((session: Session) => {
			this.sessionStartedAt = DateTime.now();
			this.updateRefreshParams(session);

			if (session.expiresAt) {
				const sessionExpiresAt: DateTime = DateTime.fromISO(session.expiresAt);
				this.setEarlyRefreshTimeout(sessionExpiresAt);
				this.setExpirationWarningTimeout(sessionExpiresAt);
				this.setExpirationTimeout(sessionExpiresAt);
			}
		});
	}

	private updateRefreshParams(session: Session): void {
		if (session.partner.id === environment.wfPartnerId) {
			this.earlyRefreshEnabled = true;
			this.shouldSendWFKeepAliveRequests = true;
		} else {
			this.earlyRefreshEnabled = false;
			this.shouldSendWFKeepAliveRequests = false;
		}
	}

	private setEarlyRefreshTimeout(sessionExpiresAt: DateTime): void {
		this.earlyRefreshTimeoutExpired = false;
		clearTimeout(this.earlyRefreshTimeout);

		if (!this.earlyRefreshEnabled) {
			return;
		}

		if (this.earlyRefreshTimeoutDuration >= sessionExpiresAt.minus(this.expirationWarningDuration).diffNow()) {
			console.warn('Session early refresh timeout duration is too long; skipping session early refresh timeout setup.');
			return;
		}

		this.earlyRefreshTimeout = setTimeout(() => {
			if (this.isUserActive()) {
				this.refreshSession();
				return;
			}

			this.earlyRefreshTimeoutExpired = true;
		}, this.earlyRefreshTimeoutDuration.as('milliseconds'));
	}

	private setExpirationWarningTimeout(sessionExpiresAt: DateTime): void {
		this.expirationWarningTimeoutExpired = false;
		clearTimeout(this.expirationWarningTimeout);

		if (sessionExpiresAt < DateTime.now()) {
			return;
		}

		this.expirationWarningTimeout = setTimeout(() => {
			if (this.isUserActive()) {
				this.refreshSession();
				return;
			}

			this.expirationWarningTimeoutExpired = true;
			this.expirationWarningSnackBarRef = this.snackBar.open('For your security, this session is about to expire due to inactivity.', 'Continue session', {
				panelClass: 'info-snackbar',
			});
		}, sessionExpiresAt.minus(this.expirationWarningDuration).diffNow().as('milliseconds'));
	}

	private setExpirationTimeout(sessionExpiresAt: DateTime): void {
		clearTimeout(this.expirationTimeout);
		this.expirationTimeout = setTimeout(() => {
			this.userActivitySubscription.unsubscribe();
			this.snackBar.open('Your session has expired due to inactivity.', undefined, { panelClass: 'warning-snackbar' });
		}, sessionExpiresAt.diffNow().as('milliseconds'));
	}

	private isUserActive(): boolean {
		const cutoff: DateTime = DateTime.max(DateTime.now().minus(this.userActivityWindow), this.sessionStartedAt);
		return this.userLastActiveAt > cutoff;
	}

	private refreshSession(): void {
		this.sessionService.refreshSession();

		if (this.shouldSendWFKeepAliveRequests) {
			this.sendWFKeepAliveRequest();
		}
	}

	private sendWFKeepAliveRequest(): void {
		this.httpClient
			.get(environment.wfKeepAliveUrl, {
				params: {
					appid: 'wfapptir',
					randomvalue: Math.random().toString(36).substring(2),
				},
				withCredentials: true,
				responseType: 'text',
			})
			.subscribe({
				error: (error: unknown) => {
					console.warn('WF Keep-Alive heartbeat failed');
					console.warn(error);
				},
			});
	}
}
